import React, { useState } from 'react';
import { Modal } from '@pulsarml/ui.react.components';
import { translate } from '@utils/i18n';
import { Clock, Cpu, Calendar } from 'lucide-react';
import { Banner } from '../Banner';
import { AnnotateStopErrorModalData } from '@clean-code/ducks/modal';
import { AxiosError } from 'axios';
import { useStopOptions } from '@clean-code/pages/RealTimePageV2/components/StopsTable/useStopOptions';
import { cn } from '@utils/tailwind-class.merge';
import { useEquipmentColumnTitle } from '@clean-code/hooks';
import { useUpdateStop } from '@clean-code/hooks/useUpdateStop';
import { useHasPermission } from '@clean-code/hooks/usePermissions';
import { TouchedStop } from '@clean-code/types/Stop';
import { buildComparisonData } from './util';
import { OverwritingStopClassificationError } from './types';

interface AnnotateStopErrorModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  data?: AnnotateStopErrorModalData;
}

export const AnnotateStopErrorModal: React.FC<AnnotateStopErrorModalProps> = ({
  isOpen: controlledOpen,
  onClose,
  data
}) => {
  const [internalOpen, setInternalOpen] = useState(true);
  const equipmentColumnTitle = useEquipmentColumnTitle();
  // Use controlled open state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleClose = onClose || (() => setInternalOpen(false));
  // Overriding replaces a cause another save just stored, which takes `stop.annotation.modify`.
  const canOverride = useHasPermission()('stop', 'annotation.modify');
  const updateStop = useUpdateStop({
    forceUpdate: true,
    origin: data?.origin,
    trackSuccess: data?.trackSuccess
  });
  // Use provided data or fallback to default
  const stopInfo = data?.stopInfo;
  const { generalCauseOptions, components, getSpecificCauses } = useStopOptions(
    stopInfo?.machineId ?? 0
  );
  const overwritingStopClassificationError = (data?.error as AxiosError)
    .response?.data as OverwritingStopClassificationError;
  const comparisonData = buildComparisonData(
    overwritingStopClassificationError,
    generalCauseOptions,
    components,
    getSpecificCauses
  );

  return (
    <Modal
      show={open}
      title={translate('stop_annotation_error')}
      form={false}
      modalCancelAction={{
        action: handleClose,
        title: translate('close')
      }}
      modalConfirmAction={{
        action: () => {
          updateStop({ ...(stopInfo! as TouchedStop) });
          handleClose();
        },
        title: translate('override_annotation'),
        type: 'primary',
        disabled: !canOverride
      }}
    >
      <div className="tw-space-y-3 -tw-my-4 tw-font-sans">
        {/* Error Alert Section */}
        <Banner
          text={translate('stop_annotation_error_description')}
          variant="warning"
        />
        {/* Stop Information */}
        <div className="tw-pl-3">
          <p className="tw-text-base tw-text-gray-500 tw-mb-5">
            {translate('stop_information_description')}
          </p>

          <div className="tw-space-y-4">
            <div className="tw-flex tw-items-center tw-gap-3">
              <Cpu className="tw-w-5 tw-h-5 tw-text-gray-400" />
              <span className="tw-text-base tw-text-gray-600">
                {translate('machine')}:
              </span>
              <span className="tw-text-base tw-font-medium tw-text-gray-900">
                {stopInfo?.machine}
              </span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
              <Calendar className="tw-w-5 tw-h-5 tw-text-gray-400" />
              <span className="tw-text-base tw-text-gray-600">
                {translate('start')}:
              </span>
              <span className="tw-text-base tw-font-medium tw-text-gray-900">
                {stopInfo?.stopStartTimeHHmm}
              </span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
              <Clock className="tw-w-5 tw-h-5 tw-text-gray-400" />
              <span className="tw-text-base tw-text-gray-600">
                {translate('duration')}:
              </span>
              <span className="tw-text-base tw-font-medium tw-text-gray-900">
                {stopInfo?.stopDuration}
              </span>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="tw-bg-white tw-overflow-hidden">
          <div className="tw-divide-y tw-divide-gray-50">
            {/* Header */}
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-3 tw-py-2">
              <div className="tw-text-sm tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
                {translate('field')}
              </div>
              <div className="tw-text-sm tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-text-center">
                {translate('current_annotation')}
              </div>
              <div className="tw-text-sm tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-text-center">
                {translate('your_annotation')}
              </div>
            </div>
            {/* Rows */}
            {comparisonData.map((row, index) => (
              <div
                key={index}
                className="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-3 tw-py-2 hover:tw-bg-gray-50/50 tw-transition-colors"
              >
                <div className="tw-text-base tw-text-gray-600 tw-flex tw-items-center">
                  {row.field === 'equipment'
                    ? equipmentColumnTitle
                    : translate(row.field)}
                </div>
                <div
                  className={cn(
                    'tw-text-base tw-text-gray-900 tw-text-center tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-h-10',
                    row.current !== row.new
                      ? 'tw-border-emerald-200'
                      : 'tw-border-slate-200'
                  )}
                >
                  {row.current}
                </div>
                <div
                  className={cn(
                    'tw-text-base tw-text-gray-900 tw-text-center tw-px-3 tw-py-1.5 tw-rounded-lg tw-border',
                    row.current !== row.new
                      ? 'tw-border-emerald-200'
                      : 'tw-border-slate-200'
                  )}
                >
                  {row.new}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
